/*
  Start-up (ADR 0020, replacing ADR 0005's offline branches):

    load /config.json → check-sso with a timeout → branch:

    session              proceed; refresh timer on                         'online'
    no session           redirect to login                                 'login'
    no answer in time    "the sign-in service did not answer", retry       'unreachable'

  Stub mode (no Keycloak URL) is a fourth branch for local development and Playwright: the
  API's development stub authenticates every request.

  There is no cached session, no PIN gate and no offline mode: the app is a desktop web app
  that needs a connection, as every other Lumen application does.
*/
import { loadRuntimeConfig } from '../runtimeConfig'
import { initKeycloak, isKeycloakConfigured, login, startTokenRefresh } from './keycloak'

export type BootstrapResult =
  { mode: 'stub' } | { mode: 'online' } | { mode: 'login' } | { mode: 'unreachable' }

export interface BootstrapDeps {
  loadConfig: typeof loadRuntimeConfig
  init: typeof initKeycloak
  configured: typeof isKeycloakConfigured
  redirectToLogin: typeof login
  startRefresh: typeof startTokenRefresh
}

const defaults: BootstrapDeps = {
  loadConfig: loadRuntimeConfig,
  init: initKeycloak,
  configured: isKeycloakConfigured,
  redirectToLogin: login,
  startRefresh: startTokenRefresh,
}

export async function bootstrap(overrides: Partial<BootstrapDeps> = {}): Promise<BootstrapResult> {
  const deps = { ...defaults, ...overrides }

  await deps.loadConfig()

  if (!deps.configured()) {
    return { mode: 'stub' }
  }

  const outcome = await deps.init()

  if (outcome === 'authenticated') {
    deps.startRefresh()
    return { mode: 'online' }
  }

  if (outcome === 'anonymous') {
    deps.redirectToLogin()
    return { mode: 'login' }
  }

  return { mode: 'unreachable' }
}
